// Simulate network latency for mock APIs
const LATENCY = 500;

export interface Beneficiary {
  receiverId: string;
  receiverFirstName: string;
  receiverLastName: string;
  receiverAccountNumber: string;
  receiverBank: string;
  receiverCurrency: string;
  receiverEmail: string;
  receiverCountry: string;
}

export const initiateMoveMoney = async (): Promise<{ transactionId: string }> => {
  console.log('API Call: Initiating money transfer...');
  return new Promise(resolve => {
    setTimeout(() => {
      const transactionId = `txn_${Date.now()}`;
      console.log('API Response: transactionId:', transactionId);
      resolve({ transactionId });
    }, LATENCY);
  });
};

export const validateBeneficiary = async (beneficiaryId: string): Promise<{ success: boolean }> => {
  console.log('API Call: Validating beneficiary...', { beneficiaryId });
  return new Promise(resolve => {
    setTimeout(() => {
      const success = beneficiaryId !== '';
      console.log('API Response: Beneficiary valid:', success);
      resolve({ success });
    }, LATENCY);
  });
};

export const validateAmount = async (amount: string): Promise<{ success: boolean }> => {
  console.log('API Call: Validating amount...', { amount });
  return new Promise(resolve => {
    setTimeout(() => {
      const success = parseFloat(amount) > 0;
      console.log('API Response: Amount valid:', success);
      resolve({ success });
    }, LATENCY);
  });
};

export const validateAccount = async (fromAccount: string, amount: string): Promise<{ success: boolean }> => {
  console.log('API Call: Validating source account...', { fromAccount, amount });
  return new Promise(resolve => {
    setTimeout(() => {
      const success = fromAccount !== '';
      console.log('API Response: Account valid:', success);
      resolve({ success });
    }, LATENCY);
  });
};

export const confirmTransaction = async (
  transactionDetails: object
): Promise<{ success: boolean; confirmationNumber: string }> => {
  console.log('API Call: Confirming transaction...', transactionDetails);
  const response = await confirmMoneyMovement(transactionDetails);
  return {
    success: response.success,
    confirmationNumber: response.coreTransactionID || `MOCK_CONF_${Date.now()}`
  };
};

export const confirmMoneyMovement = async (details: any): Promise<any> => {
  try {
    console.log('API Request: confirmMoneyMovement (Step 5)', details);

    const response = await fetch(
      'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-external-transfer/conv-banking/money-movement',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cookie': 'dtCookie=v_4_srv_5_sn_AE58FE52A7FB95E4EA7A4E1B9734E1B0_perc_100000_ol_0_mul_1_app-3Aea7c4b59f27d43eb_1'
        },
        body: JSON.stringify(details)
      }
    );

    if (!response.ok) {
      throw new Error(`Step 5 API call failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('API Response: confirmMoneyMovement', data);

    return {
      ...data,
      success: true
    };
  } catch (error) {
    console.error('Error in confirmMoneyMovement:', error);
    return {
      success: false,
      message: 'Failed to confirm transaction.'
    };
  }
};


export const fetchAccounts = async (customerId?: string): Promise<{
  creditCard?: any[];
  checking?: any[];
  savings?: any[];
}> => {
  const userId = customerId || localStorage.getItem('currentUserId') || 'nova.brooks';
  console.log('API Call: Fetching accounts for customer...', { userId });

  try {
    const response = await fetch(
      'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-account/accounts/v2/balances-by-customerId',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cookie': 'dtCookie=mock-cookie-value'
        },
        body: JSON.stringify([{ customerId: userId }])
      }
    );

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    console.log('API Response: Accounts data:', data);
    return data;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

// Optional: fallback fetchBeneficiaries in case needed for legacy
export const fetchBeneficiaries = async (payeeId?: string): Promise<any[]> => {
  console.warn('Warning: fetchBeneficiaries() is deprecated in favor of initializeMoneyMovementJourney');
  return [];
};

export const submitPaymentMethod = async (
  paymentMethod: string,
  transferDetails: {
    amount: string;
    accountNumber: string;
    currency: string;
    eventMapInstanceID: string;
    sourceInstanceID: string;
  }
): Promise<any> => {
  const userId = localStorage.getItem('currentUserId') || 'nova.brooks';

  const requestBody = {
    eventMapInstanceID: transferDetails.eventMapInstanceID,
    sourceInstanceID: transferDetails.sourceInstanceID,
    isDomesticTransfer: "true",
    isInternalTransfer: false,
    isInternationalTransfer: false,
    paymentMethodRequest: {
      paymentSchedule: "OneTime",
      paymentCurrency: transferDetails.currency || "USD",
      isBusinessTxn: true,
      paymentAmount: transferDetails.amount
    },
    customerID: userId,
    transferDetails: {
      accountNumber: transferDetails.accountNumber,
      core: "finxact",
      currencyCode: transferDetails.currency || "USD",
      transferDate: new Date().toISOString().split("T")[0]
    }
  };

  console.log('API Request: submitPaymentMethod', requestBody);

  try {
    const response = await fetch(
      'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-external-transfer/conv-banking/money-movement',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cookie': 'dtCookie=mock-cookie-value'
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) throw new Error(`Failed with status ${response.status}`);
    const data = await response.json();
    console.log('API Response: submitPaymentMethod', data);
    return data;
  } catch (err) {
    console.error('Error in submitPaymentMethod:', err);
    return {
      success: false,
      message: 'Fallback: Payment method call failed'
    };
  }
};

export const initializeMoneyMovementJourney = async (
  customerID: string
): Promise<{ beneficiaries: Beneficiary[]; step2Status: any }> => {
  console.log('API Call: Step 1 - Start MM Journey', { customerID });

  const step1Response = await fetch(
    'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-external-transfer/conv-banking/money-movement',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerID })
    }
  );

  console.log('API Response: Step 1 - Start MM Journey', step1Response);

  if (!step1Response.ok) {
    throw new Error('Failed to start MM journey');
  }

  const step1Data = await step1Response.json();
  console.log('Step 1 Data:', step1Data);
  const { eventMapInstanceID, sourceInstanceID, beneficiaryList = [] } = step1Data;

  if (!eventMapInstanceID || !sourceInstanceID) {
    throw new Error('Missing required instance IDs');
  }

  const beneficiaries: Beneficiary[] = beneficiaryList.map((b: any) => ({
    receiverId: b.receiverId,
    receiverFirstName: b.beneficiaryFirstName || b.beneficiaryName?.split(' ')[0] || '',
    receiverLastName: b.beneficiaryLastName || b.beneficiaryName?.split(' ').slice(1).join(' ') || '',
    receiverAccountNumber: b.beneficiaryAccountNumber,
    receiverBank: '',
    receiverCurrency: b.beneficiaryCurrency,
    receiverEmail: b.beneficiaryEmail,
    receiverCountry: b.beneficiaryCountry
  }));

  const step2Payload = {
    accountNumberList: [
      "400111651404", 
      "000010118896" 
    ],
    customerID,
    eventMapInstanceId: eventMapInstanceID,
    sourceInstanceId: sourceInstanceID
  };

  const step2Response = await fetch(
    'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-external-transfer/conv-banking/money-movement',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(step2Payload)
    }
  );

  if (!step2Response.ok) {
    throw new Error('Failed to continue MM journey');
  }

  const step2Data = await step2Response.json();

  // return { beneficiaries, step2Status: step2Data };
  return {
    beneficiaries,
    step2Status: {
      ...step2Data,
      eventMapInstanceID: step2Data.eventMapInstanceID || eventMapInstanceID,
      sourceInstanceID: step2Data.sourceInstanceID || sourceInstanceID
    }
  };
};

export const fetchAvailablePaymentMethods = async (
  transferDetails: {
    amount: string;
    accountNumber: string;
    currency: string;
    eventMapInstanceId: string;
    sourceInstanceId: string;
  }
): Promise<any> => {
  const userId = localStorage.getItem('currentUserId') || 'nova.brooks';

  const requestBody = {
    eventMapInstanceId: transferDetails.eventMapInstanceId,
    sourceInstanceId: transferDetails.eventMapInstanceId,
    isDomesticTransfer: "true",
    isInternalTransfer: false,
    isInternationalTransfer: false,
    paymentMethodRequest: {
      paymentSchedule: "OneTime",
      paymentCurrency: transferDetails.currency,
      isBusinessTxn: true,
      paymentAmount: transferDetails.amount
    },
    customerID: userId,
    transferDetails: {
      accountNumber: transferDetails.accountNumber,
      core: "finxact",
      currencyCode: transferDetails.currency,
      transferDate: new Date().toISOString().split("T")[0]
    }
  };

  const response = await fetch(
    'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-external-transfer/conv-banking/money-movement',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch payment methods: ${response.status}`);
  }

  const data = await response.json();
  return data; // contains updated eventMapInstanceID & sourceInstanceID
};

export const validatePaymentMethodSelection = async (payload: any): Promise<any> => {
  try {
    const response = await fetch(
      'https://csp.az-demo.bcm.industrycloud.pwc.com/cc-external-transfer/conv-banking/money-movement',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) throw new Error(`Step 4 validation failed: ${response.status}`);
    const data = await response.json();
    console.log('Step 4 response:', data);

    return {
      ...data,
      eventMapInstanceID: data.eventMapInstanceID,
      sourceInstanceID: data.sourceInstanceID
    };
  } catch (err) {
    console.error('Error in validatePaymentMethodSelection:', err);
    throw err;
  }
};

